import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  ShieldCheck,
  Activity,
  AlertTriangle,
  Eye,
  Landmark,
  Target,
  Clock,
} from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { getPublicArticles } from "@/lib/articles";

const SITE = "https://xauproacademy.com";

const PROP_CATEGORIES = new Set(["Prop Firm Trading", "Psychology", "Trading Plan"]);

export const Route = createFileRoute("/prop-firm-gold")({
  head: () => ({
    meta: [
      {
        title: "Prop Firm Gold Trading: Governed XAUUSD | XAU Pro Academy",
      },
      {
        name: "description",
        content:
          "Governed XAUUSD decisions built for funded and evaluation-stage gold traders. Selectivity, macro defence and rules-compatible drawdown discipline.",
      },
      {
        name: "keywords",
        content:
          "prop firm gold trading, prop firm XAUUSD, funded account gold trading, prop firm gold strategy, drawdown gold trading, consistency rule gold",
      },
      {
        property: "og:title",
        content: "Prop Firm Gold Trading: Governed XAUUSD Decisions",
      },
      {
        property: "og:description",
        content:
          "Built for funded gold traders: selectivity, macro defence and validated reasoning compatible with prop-firm rules.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/prop-firm-gold` },
    ],
    links: [{ rel: "canonical", href: `${SITE}/prop-firm-gold` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Prop Firm Gold Trading",
          url: `${SITE}/prop-firm-gold`,
          description:
            "Governed XAUUSD decisions for funded and evaluation-stage gold traders.",
          isPartOf: { "@type": "WebSite", name: "XAU Pro Academy", url: SITE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Prop Firm Gold", item: `${SITE}/prop-firm-gold` },
          ],
        }),
      },
    ],
  }),
  component: PropFirmGoldPage,
});

function PropFirmGoldPage() {
  const reads = getPublicArticles()
    .filter((a) => PROP_CATEGORIES.has(a.category))
    .slice(0, 6);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div
          className="pointer-events-none absolute -top-40 left-1/2 h-[32rem] w-[56rem] -translate-x-1/2 rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(closest-side, hsl(var(--gold) / 0.45), transparent)" }}
        />
        <div className="container relative mx-auto max-w-6xl px-4 pb-20 pt-20 md:pt-28">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center rounded-full border border-gold/30 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
              Prop Firm Gold Trading
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold tracking-tight md:text-6xl">
              Built for funded <span className="text-gradient-gold">gold traders</span>,
              not signal followers
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
              Prop-firm rules reward selectivity, drawdown discipline and consistency. Random
              signal feeds reward frequency. The platform publishes governed XAUUSD decisions
              with the macro defence layer and reject discipline that funded accounts require.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button asChild variant="gold" size="lg">
                <Link to="/pricing">Membership options</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/validation">See Validation Hub</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pain to answer */}
      <section className="container mx-auto max-w-6xl px-4 py-16">
        <div className="grid gap-8 md:grid-cols-2">
          <div className="rounded-2xl border border-border/60 bg-card/60 p-7">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <h2 className="mt-4 font-display text-2xl font-bold">Where funded accounts fail</h2>
            <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-destructive" />
                <span>
                  Overtrading during low-liquidity sessions, breaching daily drawdown. Size every
                  trade with the{" "}
                  <Link to="/position-size-calculator" className="text-gold hover:underline">
                    gold position size calculator
                  </Link>
                  .
                </span>
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-destructive" />
                Holding through high-impact news without a deterministic stand-down rule.
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-destructive" />
                Following high-frequency signal rooms that ignore consistency rules.
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-destructive" />
                Revenge trading after a stop-out instead of pausing exposure.
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-destructive" />
                Trusting opaque providers with no public reject record.
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-gold-muted/30 via-card to-card p-7">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold/20 text-gold">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <h2 className="mt-4 font-display text-2xl font-bold">How the platform answers</h2>
            <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" />
                Selectivity over frequency. Low-quality conditions are rejected, not traded.
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" />
                Macro defence layer pauses publication around high-impact catalysts.
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" />
                Circuit-breaker philosophy: paused output is preferred over reckless output.
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" />
                Every decision is published with reasoning and graded for quality.
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" />
                A public audit trail you can verify before allocating funded risk.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Risk calculator showcase */}
      <section className="container mx-auto max-w-6xl px-4 pb-16">
        <div className="grid items-center gap-8 rounded-2xl border border-gold/30 bg-gradient-to-br from-gold-muted/30 via-card to-card p-7 md:grid-cols-[1.4fr_1fr] md:p-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Free tool</div>
            <h2 className="mt-3 font-display text-3xl font-bold">Gold position size and daily drawdown calculator</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Convert account size, risk percentage and stop distance into an exact XAUUSD lot.
              Prop firm mode shows how much of your daily loss limit the next trade consumes,
              before you place it.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" />Live gold price fills the entry automatically.</li>
              <li className="flex gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" />Daily room check with clear safe, caution and danger states.</li>
              <li className="flex gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" />Warns when a gold stop is too tight for normal volatility.</li>
            </ul>
          </div>
          <div className="flex md:justify-end">
            <Button asChild variant="gold" size="lg">
              <Link to="/position-size-calculator">
                Open the calculator <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why funded traders fit */}
      <section className="border-y border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-6xl px-4 py-16">
          <div className="mx-auto max-w-2xl text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
              Why this is a natural fit
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold md:text-4xl">
              The same discipline your prop firm requires
            </h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {[
              {
                icon: Target,
                label: "Selectivity",
                body: "Funded accounts compound through fewer high-quality decisions, not signal volume. Selectivity is the product.",
              },
              {
                icon: Activity,
                label: "Validation",
                body: "Orion is validated publicly on historical XAUUSD data. You evaluate the engine before you commit funded risk.",
              },
              {
                icon: Eye,
                label: "Audit",
                body: "Per-decision audit, including rejects. Nothing is curated. Nothing is hidden after the fact.",
              },
            ].map((c) => (
              <div key={c.label} className="rounded-xl border border-border/60 bg-card p-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold-muted text-gold">
                  <c.icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-display text-lg font-bold">{c.label}</div>
                <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prop-firm focused reads */}
      {reads.length > 0 && (
        <section className="container mx-auto max-w-6xl px-4 py-16">
          <div className="flex items-end justify-between border-b border-border/60 pb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Reference reads
              </div>
              <h2 className="mt-1 font-display text-2xl font-bold md:text-3xl">
                Drawdown, consistency and execution
              </h2>
            </div>
            <Link
              to="/education"
              className="hidden text-sm font-semibold text-gold hover:underline md:inline"
            >
              All articles <ArrowRight className="inline h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {reads.map((a) => (
              <Link
                key={a.slug}
                to="/education/$slug"
                params={{ slug: a.slug }}
                className="group flex flex-col rounded-xl border border-border/60 bg-card p-6 transition hover:border-gold/40"
              >
                <div className="text-xs font-semibold uppercase tracking-wider text-gold">
                  {a.category}
                </div>
                <h3 className="mt-3 font-display text-lg font-bold leading-snug">{a.title}</h3>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{a.excerpt}</p>
                <div className="mt-5 flex items-center justify-between border-t border-border/60 pt-4 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {a.readTime}
                  </span>
                  <span className="inline-flex items-center gap-1 font-semibold text-gold">
                    Read <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Trust Chain handoff */}
      <section className="border-t border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-16">
          <div className="mx-auto max-w-3xl text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
              Before you allocate funded risk
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold md:text-4xl">
              Evaluate the engine first
            </h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                to: "/signals" as const,
                icon: Activity,
                label: "Published Decisions",
                body: "See current and recent decisions, with full reasoning and grading.",
              },
              {
                to: "/validation" as const,
                icon: ShieldCheck,
                label: "Validation Hub",
                body: "Orion validated on historical XAUUSD data, transparent methodology.",
              },
              {
                to: "/track-record" as const,
                icon: Eye,
                label: "Track Record",
                body: "Per-decision audit. Wins, losses, and rejects, all visible.",
              },
              {
                to: "/orion" as const,
                icon: Landmark,
                label: "Orion Engine",
                body: "The decision engine, the macro defence layer, and the rules behind it.",
              },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="group flex flex-col rounded-xl border border-border/60 bg-card p-6 transition hover:border-gold/40"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold-muted text-gold">
                  <c.icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-display text-lg font-bold">{c.label}</div>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{c.body}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-gold">
                  Open <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/pricing">Membership options</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/faq">Methodology FAQ</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
